import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Package, Plus, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ProductDialog from "@/components/ProductDialog";
import ProductDetail from "@/components/ProductDetail";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Prepend backend host to the relative /api/uploads/* URL from the API.
export const productImgSrc = (url) => (url ? `${BACKEND}${url}` : "");

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="products-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Catalogue</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">Products</h1>
          <p className="text-slate-500 mt-1 text-sm">Everything the team can pitch. SuperAdmin can add new products and drop in supporting files.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreating(true)} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-2" /> New Product
          </Button>
        )}
      </header>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : products.length === 0 ? (
        <div className="solid-panel p-12 text-center">
          <Package className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">No products yet.</p>
          {isAdmin && <p className="text-xs text-slate-400 mt-1">Click "New Product" to add the first one.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map((p) => (
            <button
              key={p.id}
              onClick={() => setDetail(p)}
              className="solid-panel overflow-hidden text-left hover:border-[#1D63ED] transition-colors group"
              data-testid={`product-card-${p.id}`}
            >
              <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img src={productImgSrc(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <ImageIcon className="w-10 h-10 text-slate-300" />
                )}
              </div>
              <div className="p-4">
                <p className="font-medium text-sm text-slate-900 truncate">{p.name}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[32px]">
                  {p.description || <span className="italic text-slate-400">No description yet.</span>}
                </p>
                <p className="text-[11px] text-slate-400 mt-2">
                  {(p.files?.length || 0)} file{p.files?.length === 1 ? "" : "s"}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      <ProductDialog
        open={creating || !!editing}
        product={editing}
        onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); } }}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />
      <ProductDetail
        product={detail}
        open={!!detail}
        onOpenChange={(v) => !v && setDetail(null)}
        isAdmin={isAdmin}
        onEdit={(p) => { setDetail(null); setEditing(p); }}
        onDeleted={() => { setDetail(null); load(); }}
        onChanged={(refreshed) => { setDetail(refreshed); load(); }}
      />
    </div>
  );
}
